import React from 'react';
import {
  Button,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import useStyles, { styles } from './styles';
import type { RefinanceGridProps, PrepayRequestData, RefinancePolicyDetail } from '../../../types/account';
import { prepayTypeLabels, prepayStatusLabels } from './labels';
import { getLastPrepayRequests } from '../../../queries/account';
import analytics from '../../../utils/analytics';
import { parseAmountWithCurrency } from '../../../utils/helpers';

function RefinanceGrid(props: RefinanceGridProps) {
  const {
    unpaidAmount, paidAmount, token, loanState, loanId,
    refinancePolicy,
    currency,
  } = props;
  const classes = useStyles();
  const history = useHistory();
  const [lastPrepayRequestData, setLastPrepayRequestsData] = React.useState<PrepayRequestData['prepayRequest']|undefined>(undefined);

  const [totalToDeposit, setTotalToDeposit] = React.useState(0);

  const goToRefinance = (type:string) => {
    setTimeout(() => history.push(`/account/refinance/${loanId}/${type}`), 500);
  };

  const {
    lastPrepayRequestsData,
    lastPrepayRequestDataLoading,
    lastPrepayRequestDataError,
  } = getLastPrepayRequests(token);

  React.useEffect(() => {
    if (lastPrepayRequestsData.length > 0) {
      const newLastPrepayRequestData = lastPrepayRequestsData.find(
        (request) => request.prepayRequest?.prepaidLoanId === loanId,
      );
      if (newLastPrepayRequestData && newLastPrepayRequestData.prepayRequest && newLastPrepayRequestData.prepayRequest.type !== 'fullPrepay') {
        analytics.page('PLR - TU CREDITO', 'REFINANCIAMIENTO PENDIENTE');
        setTotalToDeposit(newLastPrepayRequestData.totalToDeposit);
      } else {
        analytics.page('PU - TU CREDITO', 'SOLICITAR REFINANCIAMIENTO');
      }
      setLastPrepayRequestsData(newLastPrepayRequestData?.prepayRequest);
    }
  }, [lastPrepayRequestsData]);

  if (lastPrepayRequestDataLoading) {
    return (
      <Box sx={styles.boxContainer}>
        <Typography sx={{ fontWeight: 'bold' }}>Estamos consultando tus datos...</Typography>
        <br />
        <CircularProgress style={{ fontSize: 50 }} />
      </Box>
    );
  }
  if (lastPrepayRequestDataError) {
    return (
      <Box sx={styles.boxContainer}>
        <Typography color="error" variant="caption" component="div">Ha ocurrido un error. Por favor contáctanos</Typography>
      </Box>
    );
  }
  if (lastPrepayRequestData && lastPrepayRequestData.type !== 'fullPrepay') {
    return (
      <Box sx={styles.boxContainer}>
        <Typography variant="body1">¡Tienes una solicitud de refinanciamiento pendiente!</Typography>
        <div style={{ backgroundColor: 'whitesmoke', padding: 15, borderRadius: 15 }}>
          <div className={classes.prepayDetail}>
            <Typography color="primary" variant="body2" sx={{ fontWeight: 'bold' }}>Tipo de solicitud: </Typography>
            <Typography color="secondary" variant="body2" align="right" display="inline">
              {prepayTypeLabels[lastPrepayRequestData.type]}
            </Typography>
          </div>
          <div className={classes.prepayDetail}>
            <Typography color="primary" align="left" variant="body2" sx={{ fontWeight: 'bold' }}>Origen solicitud: </Typography>
            <Typography color="secondary" variant="body2" align="right" display="inline">
              {prepayStatusLabels[lastPrepayRequestData.status]}
            </Typography>
          </div>
          <div className={classes.prepayDetail}>
            <Typography color="primary" variant="body2" sx={{ fontWeight: 'bold' }}>Meses de gracia: </Typography>
            <Typography color="secondary" variant="body2" align="right" display="inline">
              {lastPrepayRequestData.gracePeriods}
              {' '}
              {lastPrepayRequestData.gracePeriods > 1 ? <span>meses</span> : <span>mes</span>}
            </Typography>
          </div>
          <div className={classes.prepayDetail}>
            <Typography color="primary" variant="body2" sx={{ fontWeight: 'bold' }}>Monto a refinanciar: </Typography>
            <Typography color="secondary" variant="body2" align="right" display="inline">
              {parseAmountWithCurrency(currency, totalToDeposit)}
            </Typography>
          </div>
          {lastPrepayRequestData.type === 'cashOut' && (
            <div className={classes.prepayDetail}>
              <Typography color="primary" variant="body2" sx={{ fontWeight: 'bold' }}>Monto extra: </Typography>
              <Typography color="secondary" variant="body2" align="right" display="inline">
                {parseAmountWithCurrency(currency, lastPrepayRequestData.cashOut.amount)}
              </Typography>
            </div>
          )}
        </div>
        <br />
        <Typography color="primary" variant="body2" align="center" fontStyle="italic">
          Estamos revisando tu solicitud.
          <br />
          ¡Te contactaremos cuando esté lista!
        </Typography>
      </Box>
    );
  }

  const getDisabledText = (policy: RefinancePolicyDetail) => {
    if (policy.allowedLoanStates.length === 0 || policy.paidLoanPercentage === 1) {
      return <>No se permiten refinanciamientos para este crédito.</>;
    }
    if (!policy.allowedLoanStates.includes(loanState)) {
      return (
        <>
          Opción deshabilitada para créditos en estado
          {' '}
          <b>Moroso</b>
          {' '}
          .
        </>
      );
    }
    if (paidAmount / (unpaidAmount + paidAmount) < policy.paidLoanPercentage) {
      return (
        <>
          Debes pagar al menos
          {' '}
          <b>
            {policy.paidLoanPercentage * 100}
            % de tu crédito
          </b>
          {' '}
          actual para solicitar más dinero.
        </>
      );
    }
    return null;
  };
  const cashoutDisabledText = getDisabledText(refinancePolicy.cashout);
  const refinanceRPDisabledText = getDisabledText(refinancePolicy.refinanceRP);
  if (!!cashoutDisabledText && !!refinanceRPDisabledText) {
    return (
      <Box sx={styles.boxContainer}>
        <Typography variant="body1">
          Lamentablemente, en este momento
          {' '}
          <b>NO</b>
          {' '}
          está permitido solicitar refinanciamiento para tu crédito.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.boxContainer}>
      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
        ¿Qué tipo de refinanciamiento quieres solicitar?
      </Typography>
      <Box sx={{ width: '100%' }}>
        <Button
          variant="contained"
          color="primary"
          sx={{ marginTop: 2.5, width: '100%' }}
          onClick={() => goToRefinance('rate-period')}
          disabled={!!refinanceRPDisabledText}
        >
          Ajustar mi pago mensual
          {refinanceRPDisabledText
          && (
          <Typography
            variant="caption"
            color="text.secondary"
          >
            {refinanceRPDisabledText}
          </Typography>
          )}
        </Button>
      </Box>
      <Box sx={{ width: '100%' }}>
        <Button
          variant="outlined"
          color="primary"
          sx={{ marginTop: 5, marginBottom: 1, width: '100%' }}
          onClick={() => goToRefinance('cashout')}
          disabled={!!cashoutDisabledText}
        >
          Necesito más dinero
        </Button>
        {cashoutDisabledText
          && (
          <Typography
            variant="caption"
            color="text.secondary"
          >
            {cashoutDisabledText}
          </Typography>
          )}
      </Box>
    </Box>
  );
}

export default RefinanceGrid;
